import axios from "axios";
import {
  getAuth,
} from "firebase/auth";

const API_BASE = (
  process.env.REACT_APP_BACKEND_URL ||
  "http://localhost:5000"
).replace(/\/+$/, "");

export interface VerificationTimelineItem {
  status: string;
  label: string;
  changedAt: string;
  note?: string;
}

export interface OrderVerificationData {
  verified: boolean;
  orderReference: string;
  orderType:
    | "product"
    | "delivery"
    | "accommodation"
    | "car_hire"
    | "trip";

  payment: {
    status: string;
    label: string;
    amount: number;
    currency: string;
    reference?: string;
    paidAt?: string;
    updatedAt?: string;
  };

  fulfillment: {
    status: string;
    label: string;
    updatedAt?: string;
  };

  summary: {
    title?: string;
    providerName?: string;
    scheduledAt?: string;
    maskedCustomerName?: string;
    itemCount?: number;
    pickupLocation?: string;
    destination?: string;
    vehicleName?: string;
    accommodationName?: string;
  };

  timeline:
    VerificationTimelineItem[];

  canManage?: boolean;
  allowedNextStatuses?: string[];
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

const getBearerToken = async (
): Promise<string | null> => {
  const currentUser =
    getAuth().currentUser;

  if (!currentUser) {
    return null;
  }

  return currentUser
    .getIdToken();
};

export const fetchPublicVerification =
  async (
    token: string,
  ): Promise<OrderVerificationData> => {
    const response =
      await axios.get<
        ApiResponse<OrderVerificationData>
      >(
        `${API_BASE}/api/v1/order-verifications/${encodeURIComponent(
          token,
        )}`,
      );

    return response.data.data;
  };

export const fetchManagedVerification =
  async (
    token: string,
  ): Promise<OrderVerificationData | null> => {
    const bearerToken =
      await getBearerToken();

    if (!bearerToken) {
      return null;
    }

    const response =
      await axios.get<
        ApiResponse<OrderVerificationData>
      >(
        `${API_BASE}/api/v1/order-verifications/${encodeURIComponent(
          token,
        )}/manage`,
        {
          headers: {
            Authorization:
              `Bearer ${bearerToken}`,
          },
        },
      );

    return response.data.data;
  };

export const updateVerificationStatus =
  async (
    token: string,
    status: string,
    note: string,
  ): Promise<OrderVerificationData> => {
    const bearerToken =
      await getBearerToken();

    if (!bearerToken) {
      throw new Error(
        "Please sign in to update this order.",
      );
    }

    const response =
      await axios.patch<
        ApiResponse<OrderVerificationData>
      >(
        `${API_BASE}/api/v1/order-verifications/${encodeURIComponent(
          token,
        )}/status`,
        {
          status,
          note,
        },
        {
          headers: {
            Authorization:
              `Bearer ${bearerToken}`,
          },
        },
      );

    return response.data.data;
  };
